import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useMixpanelEvent from './useMixpanelEvent';
import useCustomLoginWithRedirect from './useCustomLoginWithRedirect';
import {
  addReaction,
  removeReaction,
  getReactionCounts,
  getUserReactionsForProperty
} from '../services/ReactionService';
import { unicodeToEmoji, emojiToUnicode, REACTION_EMOJIS } from '../constants/reaction-unicode';

/**
 * useReactions hook for managing reactions using backend API
 * @param {string} reactableType - e.g., 'Property'
 * @param {string} reactableId - e.g., propertyId
 * @param {object} userState - user context
 * @param {string} pageName - The name of the page for analytics (e.g., 'Property Details', 'Property Feed - Trending')
 */
const useReactions = (reactableType, reactableId, userState, pageName = 'Property Details') => {
  const userId = userState?.id;
  const queryClient = useQueryClient();
  const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
  const { customLoginWithRedirect } = useCustomLoginWithRedirect();

  // Fetch reaction counts for this reactable
  const {
    data: reactionCountsData,
    isLoading: isCountsLoading,
    isError: isCountsError,
  } = useQuery({
    queryKey: ['reactionCounts', reactableType, reactableId],
    queryFn: () => getReactionCounts(reactableType, reactableId),
    enabled: !!reactableType && !!reactableId,
  });

  // Fetch all user reactions for listings
  const {
    data: userReactionsData,
    isLoading: isUserReactionsLoading,
    isError: isUserReactionsError,
  } = useQuery({
    queryKey: ['userReactions', userId, reactableId],
    queryFn: () => getUserReactionsForProperty(userId, reactableId),
    enabled: !!userId && !!reactableId,
  });

  // Add reaction mutation
  const addReactionMutation = useMutation({
    mutationFn: ({ reaction }) =>
      addReaction({ userId, reactableType, reactableId, reaction }),
    onSuccess: () => {
      queryClient.invalidateQueries(['reactionCounts', reactableType, reactableId]);
      queryClient.invalidateQueries(['userReactions', userId, reactableId]);
      queryClient.invalidateQueries(["user-reactions", userId, reactableId]);
    },
  });

  // Remove reaction mutation
  const removeReactionMutation = useMutation({
    mutationFn: ({ reaction }) =>
      removeReaction({ userId, reactableType, reactableId, reaction }),
    onSuccess: () => {
      queryClient.invalidateQueries(['reactionCounts', reactableType, reactableId]);
      queryClient.invalidateQueries(['userReactions', userId, reactableId]);
      queryClient.invalidateQueries(["user-reactions", userId, reactableId]);
    },
  });

  // Helper: get reactions object for this reactable (convert Unicode to emoji for display)
  const reactions = useMemo(() => {
    if (!reactionCountsData?.data) {
      // Initialize with all available reactions from constants
      const initialReactions = {};
      REACTION_EMOJIS.forEach(emoji => {
        initialReactions[emoji] = 0;
      });
      return initialReactions;
    }
    
    // Convert Unicode keys to emoji for display
    const convertedReactions = {};
    Object.entries(reactionCountsData.data).forEach(([unicode, count]) => {
      const emoji = unicodeToEmoji(unicode);
      convertedReactions[emoji] = count;
    });
    
    return convertedReactions;
  }, [reactionCountsData?.data]);

  // Helper: get user reactions for this reactable (convert Unicode to emoji)
  const userReactions = useMemo(() => {
    if (userReactionsData?.data && Array.isArray(userReactionsData.data)) {
      // userReactionsData.data is an array of { reactable_id, reaction_type (Unicode) }
      return userReactionsData.data
        .filter(r => r.reactable_id === reactableId)
        .map(r => unicodeToEmoji(r.reaction_type)); // Convert Unicode to emoji
    }
    return [];
  }, [userReactionsData?.data, reactableId]);

  // Toggle reaction (add if not present, remove if present)
  const handleReactionToggle = useCallback(
    (reaction, source) => {
      // Check if user is properly authenticated with a valid ID
      if (!userId || userId === "" || userId === "null") {
        // Redirect to signup instead of just logging a warning
        customLoginWithRedirect({
          authorizationParams: {
            redirect_uri: window.location.origin + "/social",
            screen_hint: "signup",
          },
          appState: {
            toggleProfile: true,
            showSetNicknameModal: true,
            event: {
              name: "reaction_signup_click",
              referral_code: localStorage.getItem("referral_code"),
            },
          },
        });
        return;
      }

      const alreadyReacted = userReactions.includes(reaction);
      const action = alreadyReacted ? 'remove' : 'add';
      const unicode = emojiToUnicode(reaction);
      
      if (alreadyReacted) {
        removeReactionMutation.mutate({ reaction });
      } else {
        // Only for adding reaction for now
        sendMixpanelEvent("Reaction", {
          isAuthenticated: !!userState?.id,
          user_id: userState?.id || null,
          nickname: userState?.nickname || null,
          email: userState?.email_address || null,
          pageName,
          action,
          source,
          reaction: `${reaction} - ${unicode}`,
        });
        addReactionMutation.mutate({ reaction });
      }

     
    },
    [userReactions, userId, userState?.id, userState?.nickname, userState?.email_address, pageName, sendMixpanelEvent, addReactionMutation, removeReactionMutation, customLoginWithRedirect]
  );

  return {
    reactions,
    userReactions,
    handleReactionToggle,
    isCountsLoading,
    isCountsError,
    isUserReactionsLoading,
    isUserReactionsError,
    addReactionMutation,
    removeReactionMutation,
  };
};

export default useReactions; 